import ServiceInstance from './service';
import axios, { AxiosRequestConfig } from 'axios';

type RequestConfig = AxiosRequestConfig & {
  suppressErrorLog?: (error: unknown) => boolean;
};

const controllersMap = new Map<string, AbortController>();

const request = async <T>(
  url: string,
  method: 'get' | 'post' | 'patch' | 'put' | 'delete',
  dataType: 'data' | 'params',
  data?: unknown,
  config: RequestConfig = {},
): Promise<T> => {
  const controller = new AbortController();
  const { suppressErrorLog, ...requestConfig } = config;
  requestConfig.signal = controller.signal;

  controllersMap.set(url, controller);

  try {
    const response = await ServiceInstance.request<T>({
      url,
      method,
      ...(dataType === 'params' ? { params: data } : { data }),
      ...requestConfig,
    });

    if (response === null) {
      return null as T;
    }

    if (typeof response === 'object' && response !== null && 'data' in response) {
      return response.data;
    }

    return response as T;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log(`request ${url} is being canceled`);
    } else if (!suppressErrorLog?.(error)) {
      console.error(`request ${url} failed`, error);
    }
    throw error;
  } finally {
    controllersMap.delete(url);
  }
};

export const cancelRequest = (url: string) => {
  const controller = controllersMap.get(url);
  if (controller) {
    controller.abort();
    controllersMap.delete(url);
  }
};

export const cancelAllRequests = () => {
  controllersMap.forEach((controller) => controller.abort());
  controllersMap.clear();
};

export const getByParams = <T>(url: string, data?: unknown, config?: RequestConfig) =>
  request<T>(url, 'get', 'params', data, config);
export const getByData = <T>(url: string, data?: unknown, config?: RequestConfig) =>
  request<T>(url, 'get', 'data', data, config);

export const post = <T>(url: string, data?: unknown, config?: RequestConfig) =>
  request<T>(url, 'post', 'data', data, config);

export const put = <T>(url: string, data?: unknown, config?: RequestConfig) =>
  request<T>(url, 'put', 'data', data, config);

export const patch = <T>(url: string, data?: unknown, config?: RequestConfig) =>
  request<T>(url, 'patch', 'data', data, config);

export const delParams = <T>(url: string, data?: unknown, config?: RequestConfig) =>
  request<T>(url, 'delete', 'params', data, config);
export const delByData = <T>(url: string, data?: unknown, config?: RequestConfig) =>
  request<T>(url, 'delete', 'data', data, config);

const service = {
  getByParams,
  getByData,
  post,
  put,
  patch,
  delParams,
  delByData,
};

export default service;
